import React, { useState } from 'react';
import { Dialog, DialogContent, Box, Typography, Avatar, Button, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { useWhiteLabelHiding } from '../hooks/useWhiteLabelHiding';

interface FirstTimeWelcomeModalProps {
    open: boolean;
    onClose: () => void;
}

const FirstTimeWelcomeModal: React.FC<FirstTimeWelcomeModalProps> = ({ open, onClose }) => {
    const { user, currentOrganization } = useAuth();
    const theme = useTheme();
    const [currentStep, setCurrentStep] = useState(0);
    const { shouldHidePaymentElements } = useWhiteLabelHiding();

    const greeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Bom dia';
        if (hour < 18) return 'Boa tarde';
        return 'Boa noite';
    };

    const features = [
        {
            icon: '🤖',
            title: 'Agentes de IA Personalizados',
            description: 'Crie agentes de inteligência artificial totalmente personalizados que se conectam diretamente ao WhatsApp para atender seus clientes 24/7.',
            isMain: true
        },
        {
            icon: '📱',
            title: 'Integração WhatsApp',
            description: 'Conecte-se diretamente ao WhatsApp Business API para conversas automáticas e gerenciamento inteligente de leads.'
        },
        {
            icon: '🎯',
            title: 'Dashboard Inteligente',
            description: 'Visualize todos os seus leads em tempo real com métricas avançadas e insights de performance.'
        },
        {
            icon: '👥',
            title: 'Gestão de Equipe',
            description: 'Gerencie sua equipe de vendas, atribua leads e acompanhe o desempenho de cada vendedor.'
        },
        {
            icon: '📊',
            title: 'Relatórios Avançados',
            description: 'Gere relatórios detalhados sobre conversões, qualidade dos leads e performance da equipe.'
        },
        {
            icon: '⚡',
            title: 'Performance Otimizada',
            description: 'Interface rápida e responsiva, otimizada para máxima produtividade.'
        }
    ];

    const handleNext = () => {
        if (currentStep < 2) {
            setCurrentStep(currentStep + 1);
        } else {
            onClose();
        }
    };

    const handleSkip = () => {
        onClose();
    };

    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return (
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, color: theme.palette.primary.main }}>
                            🎉 Bem-vindo ao LIA TALK!
                        </Typography>
                        <Typography variant="h6" sx={{ mb: 3, color: theme.palette.text.secondary }}>
                            {greeting()}, {user?.name?.split(' ')[0] || 'usuário'}!
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.6 }}>
                            Sua conta foi criada com sucesso! <br /><br />
                            Agora você tem acesso a uma plataforma completa de gestão de leads
                            com <strong>Agentes de IA personalizados</strong><br /> que se conectam diretamente ao WhatsApp. <br /><br />
                            Vamos fazer um tour rápido para você conhecer como criar e configurar seus agentes inteligentes.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <Button variant="outlined" onClick={handleSkip} sx={{ textTransform: 'none' }}>
                                Pular tour
                            </Button>
                            <Button variant="contained" onClick={handleNext} sx={{ textTransform: 'none', color: theme.palette.mode === 'dark' ? '#000' : undefined }}>
                                Começar tour
                            </Button>
                        </Box>
                    </Box>
                );

            case 1:
                return (
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
                            🎬 Apresentação do Sistema
                        </Typography>
                        <Box sx={{
                            position: 'relative',
                            width: '100%',
                            height: 0,
                            paddingBottom: '56.25%', // 16:9 aspect ratio
                            mb: 3,
                            borderRadius: 2,
                            overflow: 'hidden',
                            boxShadow: theme.shadows[4]
                        }}>
                            <iframe
                                width="100%"
                                height="100%"
                                src="https://www.youtube.com/embed/5Sn4JqSRqGk?si=2tXBIuG-lYFhiMkK"
                                title="YouTube video player"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                referrerPolicy="strict-origin-when-cross-origin"
                                allowFullScreen
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%'
                                }}
                            />
                        </Box>
                        <Typography variant="body2" sx={{ textAlign: 'center', color: theme.palette.text.secondary, mb: 3 }}>
                            Assista ao vídeo para conhecer todas as funcionalidades do sistema
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <Button variant="outlined" onClick={handleSkip} sx={{ textTransform: 'none' }}>
                                Pular
                            </Button>
                            <Button variant="contained" onClick={handleNext} sx={{ textTransform: 'none', color: theme.palette.mode === 'dark' ? '#000' : undefined }}>
                                Próximo
                            </Button>
                        </Box>
                    </Box>
                );

            case 2:
                return (
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
                            🤖 Agentes de IA + Funcionalidades
                        </Typography>
                        <Box sx={{ mb: 4 }}>
                            {/* Funcionalidade principal - Agentes de IA */}
                            <Box sx={{
                                p: 3,
                                borderRadius: 3,
                                border: `2px solid ${theme.palette.primary.main}`,
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.primary.main, 0.1)
                                    : alpha(theme.palette.primary.main, 0.05),
                                mb: 3,
                                position: 'relative'
                            }}>
                                <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                    <span style={{ fontSize: '2rem' }}>🤖</span>
                                    Agentes de IA Personalizados
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Crie agentes de inteligência artificial totalmente personalizados que se conectam diretamente ao WhatsApp para atender seus clientes 24/7.
                                </Typography>
                            </Box>

                            {/* Outras funcionalidades */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                {features.filter(f => !f.isMain).map((feature, index) => (
                                    <Box key={index} sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: `1px solid ${theme.palette.divider}`,
                                        backgroundColor: theme.palette.background.paper
                                    }}>
                                        <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span style={{ fontSize: '1.5rem' }}>{feature.icon}</span>
                                            {feature.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {feature.description}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <Button variant="outlined" onClick={handleSkip} sx={{ textTransform: 'none' }}>
                                Pular
                            </Button>
                            <Button variant="contained" onClick={handleNext} sx={{ textTransform: 'none', color: theme.palette.mode === 'dark' ? '#000' : undefined }}>
                                Finalizar
                            </Button>
                        </Box>
                    </Box>
                );

            default:
                return null;
        }
    };

    // Se deve ocultar elementos de pagamento, não renderizar o modal
    if (shouldHidePaymentElements) {
        return null;
    }

    return (
        <Dialog
            open={open}
            onClose={() => { }}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                    minHeight: '500px'
                }
            }}
        >
            <DialogContent sx={(theme) => ({
                p: { xs: 3, sm: 4 },
                background: theme.palette.mode === 'dark'
                    ? `radial-gradient(circle at 20% 20%, ${alpha('#1a1a1a', 0.15)} 0%, transparent 40%), radial-gradient(circle at 80% 80%, ${alpha('#1a1a1a', 0.15)} 0%, transparent 40%), linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
                    : `radial-gradient(circle at 20% 20%, ${alpha('#1a1a1a', 0.08)} 0%, transparent 40%), radial-gradient(circle at 80% 80%, ${alpha('#1a1a1a', 0.08)} 0%, transparent 40%), linear-gradient(135deg, #FAFBFC 0%, #F4F6F8 100%)`
            })}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar sx={(theme) => ({
                        bgcolor: theme.palette.mode === 'dark' ? '#65f0c5' : '#1a1a1a',
                        width: 48,
                        height: 48,
                        color: theme.palette.mode === 'dark' ? theme.palette.getContrastText('#65f0c5') : theme.palette.getContrastText('#1a1a1a')
                    })}>
                        G
                    </Avatar>
                    <Typography variant="h6" sx={(theme) => ({
                        fontWeight: 800,
                        background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, #93c5fd 0%, #c4b5fd 100%)'
                            : 'linear-gradient(135deg, #1a1a1a 0%, #4b6a89 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    })}>
                        LIA TALK • Sistema de Gestão
                    </Typography>
                </Box>

                {renderStep()}

                {/* Progress indicator */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 1 }}>
                    {[0, 1, 2].map((step) => (
                        <Box
                            key={step}
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: step <= currentStep ? theme.palette.primary.main : theme.palette.divider,
                                transition: 'background-color 0.3s ease'
                            }}
                        />
                    ))}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default FirstTimeWelcomeModal;
